/*
==============================================================================
NUPEP Cart Framework
Version 7.0.0

Renderer

Author:
NUPEP Framework

Responsibilities
----------------
✓ Render cart HTML
✓ Render totals
✓ Render shipping progress
✓ Render subscription summary
✓ Dispatch cart update event
==============================================================================
*/

"use strict";

window.NUPEP = window.NUPEP || {};

NUPEP.CartRenderer = {
  sectionId: "cart-luxury-pro",

  renderRequest: 0,
  /*
    --------------------------------------------------------------------------
    Master Renderer
    --------------------------------------------------------------------------
    */

  async render(cart) {
    if (!cart) return;

    await this.renderItems();

    this.renderTotals(cart);

    this.renderShipping(cart);

    this.renderSubscriptionSummary(cart);

    this.renderBubble(cart);
  },

  /*
    --------------------------------------------------------------------------
    Cart Items
    --------------------------------------------------------------------------
    */

  async renderItems() {
    const requestId = ++this.renderRequest;

    const sectionId = this.sectionId;

    try {
      //----------------------------------------------------------
      // Preferred: Shopify Section Rendering API
      //----------------------------------------------------------

      const root =
        window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root
          : "/";

      const response = await fetch(`${root}?sections=${sectionId}`, {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Section Rendering API failed.");
      }

      const sections = await response.json();
      if (requestId !== this.renderRequest) {
        return;
      }

      if (!sections[sectionId]) {
        throw new Error("Section not returned.");
      }

      const parser = new DOMParser();

      const documentHTML = parser.parseFromString(
        sections[sectionId],
        "text/html",
      );

      const incoming = documentHTML.querySelector("#NUPEPCartItems");

      const current = document.querySelector("#NUPEPCartItems");

      if (!incoming || !current) {
        throw new Error("Unable to locate cart container.");
      }
      const activeElement = document.activeElement;

      const activeName = activeElement?.name;

      if (current.innerHTML !== incoming.innerHTML) {
        current.replaceChildren(...incoming.childNodes);
      }
      if (activeName) {
        const replacement = current.querySelector(`[name="${activeName}"]`);

        replacement?.focus();
      }

      return;
    } catch (error) {
      console.warn(
        "[NUPEP] Section Rendering failed. Falling back to full page refresh.",
        error,
      );
    }

    //----------------------------------------------------------
    // Fallback: Reload current page HTML
    //----------------------------------------------------------
    try {
      const response = await fetch(window.location.pathname, {
        credentials: "same-origin",
      });

      if (!response.ok) {
        throw new Error("Unable to refresh cart HTML.");
      }

      const html = await response.text();

      if (requestId !== this.renderRequest) {
        return;
      }

      const parser = new DOMParser();

      const documentHTML = parser.parseFromString(html, "text/html");

      const incoming = documentHTML.querySelector("#NUPEPCartItems");

      const current = document.querySelector("#NUPEPCartItems");

      if (!incoming || !current) {
        throw new Error("Unable to locate cart container.");
      }

      const activeElement = document.activeElement;

      const activeName = activeElement?.name;

      if (current.innerHTML !== incoming.innerHTML) {
        current.replaceChildren(...incoming.childNodes);
      }

      if (activeName) {
        current.querySelector(`[name="${activeName}"]`)?.focus();
      }
    } catch (error) {
      console.error("[NUPEP Renderer]", error);
    }
  },

  /*
    --------------------------------------------------------------------------
    Totals
    --------------------------------------------------------------------------
    */

  renderTotals(cart) {
    const total = document.querySelector(".lux-total");

    if (!total) return;

    total.textContent = NUPEP.Money.format(cart.total_price);
  },

  /*
    --------------------------------------------------------------------------
    Shipping Progress
    --------------------------------------------------------------------------
    */

  renderShipping(cart) {
    const threshold = Number(window.NUPEP_FREE_SHIPPING_THRESHOLD) || 6000;

    const remaining = threshold - cart.total_price;

    const message = document.querySelector(".lux-free-ship p");

    const bar = document.querySelector(".lux-progress-bar");

    if (!message || !bar) return;

    if (remaining > 0) {
      message.innerHTML = `
                Spend
                <strong>${NUPEP.Money.format(remaining)}</strong>
                more for
                <strong>FREE UK SHIPPING</strong>
            `;

      bar.style.width = `${Math.min(
        (cart.total_price / threshold) * 100,
        100,
      )}%`;
    } else {
      message.innerHTML = "🎉 You unlocked FREE UK shipping!";

      bar.style.width = "100%";
    }
  },

  /*
    --------------------------------------------------------------------------
    Subscription Summary
    --------------------------------------------------------------------------
    */

  renderSubscriptionSummary(cart) {
    const summary = document.querySelector("#NUPEPSubscriptionSummary");

    if (!summary) return;

    const hasSubscription = cart.items.some(
      (item) =>
        item.selling_plan_allocation &&
        item.selling_plan_allocation.selling_plan,
    );

    summary.classList.toggle("has-subscription", hasSubscription);
  },

  /*
    --------------------------------------------------------------------------
    Cart Bubble
    --------------------------------------------------------------------------
    */

  renderBubble(cart) {
    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        detail: cart,
      }),
    );
    document.dispatchEvent(
      new CustomEvent("nupep:cart-rendered", {
        detail: cart,
      }),
    );
  },
};
